import { css } from 'styled-components'

import { ButtonLoaderStyled } from '../ButtonLoaderStyled'
import { CircleLoader } from '@ui/elements/Loaders'

// Shape of a `theme.colors.button.*` color-variant group. `content` is the foreground.
export interface ButtonColorTokens {
  content: {
    default: string
    hover?: string
    active?: string
    disabled?: string
  }
  background: {
    default: string
    hover?: string
    active?: string
    disabled?: string
  }
  border?: {
    default?: string
    hover?: string
    active?: string
    disabled?: string
  }
}

interface ButtonColorOptions {
  // Emit the `&[disabled]` block. Defaults to auto (on when the group has a disabled token).
  disabled?: boolean
  // Loader overlay styles: 'hidden' to hide it, or the overlay/spinner colors to show it.
  loader?: 'hidden' | { background: string; color: string }
}

const loaderCSS = (loader: NonNullable<ButtonColorOptions['loader']>) => {
  if (loader === 'hidden') {
    return css`
      ${ButtonLoaderStyled} {
        display: none;
      }
    `
  }

  return css`
    ${ButtonLoaderStyled} {
      background-color: ${loader.background};

      ${CircleLoader} {
        border-color: ${loader.color};
        border-bottom-color: transparent;
      }
    }
  `
}

// Emits the color surface for a button variant. Missing state tokens fall back:
// no `border` → tracks background; no `background.active` → reuses `background.default`.
export const buttonColorCSS = (
  tokens: ButtonColorTokens,
  options: ButtonColorOptions = {}
) => {
  const { content, background, border } = tokens
  const { disabled, loader } = options

  const borderDefault = border?.default ?? background.default
  const hoverBackground = background.hover ?? background.default
  const activeBackground = background.active ?? background.default
  const emitDisabled = disabled ?? Boolean(content.disabled || border?.disabled)

  return css`
    color: ${content.default};
    background-color: ${background.default};
    border-color: ${borderDefault};

    &:visited {
      color: ${content.default};
    }

    &:hover {
      color: ${content.hover ?? content.default};
      background-color: ${hoverBackground};
      border-color: ${border?.hover ?? border?.default ?? hoverBackground};
    }

    &:active {
      color: ${content.active ?? content.default};
      background-color: ${activeBackground};
      border-color: ${border?.active ?? border?.default ?? activeBackground};
    }

    ${emitDisabled &&
    css`
      &[disabled] {
        cursor: not-allowed;
        color: ${content.disabled ?? content.default};
        background-color: ${background.disabled ?? background.default};
        border-color: ${border?.disabled ?? borderDefault};
        box-shadow: none;

        &:hover,
        &:active {
          color: ${content.disabled ?? content.default};
          background-color: ${background.disabled ?? background.default};
          border-color: ${border?.disabled ?? borderDefault};
          box-shadow: none;
        }
      }
    `}

    ${loader && loaderCSS(loader)}
  `
}
